import React from 'react'

import Vertical from '../layouts/Vertical.jsx'
import Grid from '../components/Grid.jsx'
import Card from '../components/Card.jsx'
import Margin from '../components/Margin.jsx'
import BlockImageLabelData from '../components/BlockImageLabelData.jsx'
import LoadingState from '../components/LoadingState.jsx'

import devicePhoneIcon from '_static/img/audience-insights/icons/device-phone.png'
import deviceDesktopIcon from '_static/img/audience-insights/icons/device-desktop.png'
import deviceTabletIcon from '_static/img/audience-insights/icons/device-tablet.png'
import systemAndroidIcon from '_static/img/audience-insights/icons/system-android.png'
import systemIOSIcon from '_static/img/audience-insights/icons/system-ios.png'

import { getDeviceUsage } from '_services/insights-v2.js'
import useRequest from '../hooks/useRequest.js'
import { largestRemainderRound } from '../../charts/helpers/formatters.js'

const DEVICES = {
  MOBILE: 'Smartphone',
  COMPUTER: 'Personal computer',
  TABLET: 'Tablet',
}

const OS_TYPES = {
  ANDROID: 'Android',
  IOS: 'iOS',
}

const roundDevice = (rawData) => {
  if (!rawData) return {}

  const getDeviceDist = (type, data) =>
    data?.deviceTypes.find(({ device }) => device.toLowerCase() === type.toLowerCase())
      ?.distribution

  const data = [
    getDeviceDist(DEVICES.MOBILE, rawData),
    getDeviceDist(DEVICES.COMPUTER, rawData),
    getDeviceDist(DEVICES.TABLET, rawData),
  ]

  const total = data.reduce((acc, val) => acc + val, 0)
  const distribution = data.map((os) => (os / total) * 100)
  const rounded = largestRemainderRound(distribution)

  return {
    [DEVICES.MOBILE]: `${rounded[0]}%`,
    [DEVICES.COMPUTER]: `${rounded[1]}%`,
    [DEVICES.TABLET]: `${rounded[2]}%`,
  }
}

const roundOS = (rawData) => {
  if (!rawData) return {}

  const getOSDist = (type, data) =>
    (data?.osTypes ?? [])
      .filter(({ os }) => os.toLowerCase().includes(type.toLowerCase()))
      .reduce((acc, { userCount }) => acc + userCount, 0)

  const data = [getOSDist(OS_TYPES.ANDROID, rawData), getOSDist(OS_TYPES.IOS, rawData)]

  const total = data.reduce((acc, val) => acc + val, 0)
  const distribution = data.map((os) => (os / total) * 100)
  const rounded = largestRemainderRound(distribution)

  return {
    [OS_TYPES.ANDROID]: `${rounded[0]}%`,
    [OS_TYPES.IOS]: `${rounded[1]}%`,
  }
}

const DeviceUsage = (props) => {
  const {
    audience: { _id: audienceId },
  } = props
  const { title, description, section_1, section_2 } = props.data

  const { data, loading } = useRequest(getDeviceUsage, { audienceId }, true)

  const deviceData = roundDevice(data)
  const osData = roundOS(data)

  return (
    <Vertical
      title={title}
      description={description}
      page={props.index + 1}
      content={() => (
        <Grid
          direction="horizontal"
          gap={48}
          items={[
            {
              component: (
                <Card title={section_1.title} description={section_1.subtitle} showBorder={true}>
                  <Choose>
                    <When condition={loading}>
                      <LoadingState />
                    </When>
                    <Otherwise>
                      <Margin left={100} right={100} top={0} bottom={0}>
                        <Grid
                          direction="horizontal"
                          gap={48}
                          items={[
                            {
                              component: (
                                <BlockImageLabelData
                                  imageUrl={devicePhoneIcon}
                                  label={'MOBILE'}
                                  data={deviceData[DEVICES.MOBILE] ?? '-'}
                                />
                              ),
                              size: 0.33,
                            },
                            {
                              component: (
                                <BlockImageLabelData
                                  imageUrl={deviceDesktopIcon}
                                  label={'COMPUTER'}
                                  data={deviceData[DEVICES.COMPUTER] ?? '-'}
                                />
                              ),
                              size: 0.33,
                            },
                            {
                              component: (
                                <BlockImageLabelData
                                  imageUrl={deviceTabletIcon}
                                  label={'TABLET'}
                                  data={deviceData[DEVICES.TABLET] ?? '-'}
                                />
                              ),
                              size: 0.33,
                            },
                          ]}
                        />
                      </Margin>
                    </Otherwise>
                  </Choose>
                </Card>
              ),
              size: 0.6,
            },
            {
              component: (
                <Card title={section_2.title} description={section_2.subtitle} showBorder={true}>
                  {loading ? (
                    <LoadingState />
                  ) : (
                    <Grid
                      direction="horizontal"
                      gap={48}
                      items={[
                        {
                          component: (
                            <BlockImageLabelData
                              imageUrl={systemAndroidIcon}
                              label={'ANDROID'}
                              data={osData[OS_TYPES.ANDROID] ?? '-'}
                            />
                          ),
                          size: 0.5,
                        },
                        {
                          component: (
                            <BlockImageLabelData
                              imageUrl={systemIOSIcon}
                              label={'IOS'}
                              data={osData[OS_TYPES.IOS] ?? '-'}
                            />
                          ),
                          size: 0.5,
                        },
                      ]}
                    />
                  )}
                </Card>
              ),
              size: 0.4,
            },
          ]}
        />
      )}
    />
  )
}

export default DeviceUsage
